import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { Config } from "../types/config";
import { DEFAULT_CONFIG } from "../constants/widget-config";
import { getWidgetConfig, saveWidgetConfig } from "../services/widget";

// Helper function for deep merging configs
// Merges source INTO target, with source values taking priority
function deepMerge<T>(target: T, source: Partial<T>): T {
  const output = { ...target };
  
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      const sourceValue = (source as any)[key];
      const targetValue = (target as any)[key];
      
      // If both are objects, recursively merge
      if (isObject(sourceValue) && targetValue !== undefined && isObject(targetValue)) {
        (output as any)[key] = deepMerge(targetValue, sourceValue);
      } else if (sourceValue !== undefined) {
        // Source value takes priority (this is the saved value from API)
        (output as any)[key] = sourceValue;
      }
    });
  }
  
  return output;
}

function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

interface ConfigStore {
  // Store State
  isLoading: boolean;
  error: string | null;
  config: Config;

  // API Actions
  fetchConfig: () => Promise<void>;
  saveConfig: () => Promise<void>;
  // Utility Actions
  resetToDefaults: () => void;
  loadConfig: (config: Partial<Config>) => void;
  setConfig: (key: string, value: unknown) => void;
  setFixes: (key: string, value: unknown) => void;
  setToolbar: (key: string, value: unknown) => void;
  setChecker: (key: string, value: unknown) => void;

}

export const useConfigStore = create<ConfigStore>()(
  devtools(
    (set, get) => ({
      isLoading: true,
      error: null,
      config: DEFAULT_CONFIG,

      // API Actions
      fetchConfig: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await getWidgetConfig() as Partial<Config>;
          // Merge with defaults to handle new fields
          const mergedConfig = deepMerge(DEFAULT_CONFIG, response as Partial<Config>);
          set({ config: mergedConfig });
        } catch (error) {
          console.error(error);
          set({ error: "An unexpected error occurred" });
        } finally {
          set({ isLoading: false });
        }
      },

      saveConfig: async () => {
        set({ isLoading: true, error: null });
        try {
          const currentConfig = get().config;
          await saveWidgetConfig(currentConfig);
          set({ error: null });
        } catch (error) {
          console.error(error);
          set({ error: "Failed to save settings" });
        } finally {
          set({ isLoading: false });
        }
      },

      // Utility Actions
      resetToDefaults: () =>
        set({ config: DEFAULT_CONFIG }, false, "resetToDefaults"),

      loadConfig: (newConfig: Partial<Config>) => {
        set(
          (state) => ({ config: { ...state.config, ...newConfig } }),
          false,
          "loadConfig"
        );
      },

      setConfig: (key: string, value: unknown) => {
        set((state) => {
          const keys = key.split('.');
          const newConfig = { ...state.config };
          let current: Record<string, unknown> = newConfig;
          
          // Navigate to the parent of the target key
          for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]] as Record<string, unknown>;
          }
          
          // Set the value at the target key
          current[keys[keys.length - 1]] = value;
          
          return { config: newConfig };
        }, false, "setConfig");
      },

      setFixes: (key: string, value: unknown) => {
        set((state) => {
          const newConfig = { ...state.config };
          const keys = key.split('.');
          let current: Record<string, unknown> = newConfig.fixes;
          
          // Navigate to the parent of the target key within fixes
          for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]] as Record<string, unknown>;
          }
          
          // Set the value at the target key
          current[keys[keys.length - 1]] = value;
          return { config: newConfig };
        }, false, "setFixes");
      },

      setToolbar: (key: string, value: unknown) => {
        set((state) => {
          const newConfig = { ...state.config };
          const keys = key.split('.');
          let current: Record<string, unknown> = newConfig.toolbar;
          
          // Navigate to the parent of the target key within toolbar
          for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]] as Record<string, unknown>;
          }
          
          // Set the value at the target key
          current[keys[keys.length - 1]] = value;
          return { config: newConfig };
        }, false, "setToolbar");
      },

      setChecker: (key: string, value: unknown) => {
        set((state) => {
          const newConfig = { ...state.config };
          const keys = key.split('.');
          let current: Record<string, unknown> = newConfig.checker as Record<string, unknown>;
          
          // Navigate to the parent of the target key within checker
          for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]] as Record<string, unknown>;
          }
          
          // Set the value at the target key
          current[keys[keys.length - 1]] = value;
          return { config: newConfig };
        }, false, "setChecker");
      },
    }),
    { name: "widget-store" }
  )
);

// Legacy hook for backward compatibility
export const useWidgetSettings = () => {
  const store = useConfigStore();

  return {
    settings: {
      fixes: store.config.fixes,
    },
    setters: {
      setFixes: (fixes: Partial<Config["fixes"]>) => {
        store.loadConfig({ fixes: { ...store.config.fixes, ...fixes } as Config["fixes"] });
      },
    },
    handlers: {
      handleFixesChange: (fixes: Partial<Config["fixes"]>) => {
        store.loadConfig({ fixes: { ...store.config.fixes, ...fixes } as Config["fixes"] });
      },    
      resetToDefaults: store.resetToDefaults,
    },
  };
};